import React, { useEffect, useMemo, useRef, useState } from "react";
import type { All8PageContent } from "@apa/core-renderers";
import type { DynamicPageSelectFieldConfig, Path } from "../../Pages/pageComponentLibrary";
import type { ContentDocument } from "../../API/Models";
import { client } from "../../Clients";
import { getValueAtPath } from "./pageEditorUtils";

const PAGE_SCHEMA_KEY = "page";

type DynamicPageSelectControlProps = {
	editable: boolean;
	field: DynamicPageSelectFieldConfig;
	componentIndex: number;
	fullPath: Path;
	previewComponents: All8PageContent;
	updateComponentValue: (index: number, path: Path, value: any) => void;
	value: any;
};

const DynamicPageSelectControl: React.FC<DynamicPageSelectControlProps> = ({
	editable,
	field,
	componentIndex,
	fullPath,
	previewComponents,
	updateComponentValue,
	value
}) => {
	const [dynamicPageSearch, setDynamicPageSearch] = useState("");
	const [dynamicPages, setDynamicPages] = useState<ContentDocument[]>([]);
	const [dynamicPagesLoading, setDynamicPagesLoading] = useState(false);
	const [dynamicPagesError, setDynamicPagesError] = useState<string | null>(null);
	const dynamicPagesLoadedRef = useRef(false);

	useEffect(() => {
		if (dynamicPagesLoadedRef.current) return;
		let active = true;
		setDynamicPagesLoading(true);
		setDynamicPagesError(null);
		(async () => {
			try {
				const response = await client.content.apiContentSchemaKeyGet(PAGE_SCHEMA_KEY);
				const docs = (response.data ?? []).filter((doc) => !doc?.isDeleted);
				if (active) {
					setDynamicPages(docs);
					dynamicPagesLoadedRef.current = true;
				}
			} catch (err: any) {
				if (active) {
					setDynamicPagesError(err?.response?.data ?? err?.message ?? "Failed to load pages.");
				}
			} finally {
				if (active) setDynamicPagesLoading(false);
			}
		})();
		return () => {
			active = false;
		};
	}, []);

	const dynamicPageContentTypeKey = getValueAtPath(
		previewComponents[componentIndex],
		field.contentTypePath ?? []
	);
	const normalizedDynamicPageType = (dynamicPageContentTypeKey ?? "").toString().trim().toLowerCase();

	const filteredDynamicPages = useMemo(() => {
		if (!normalizedDynamicPageType) return [];
		const term = dynamicPageSearch.trim().toLowerCase();
		const matches = dynamicPages.filter((doc) => {
			const data = (doc.data ?? {}) as any;
			const dynamicType = (data?.dynamicContentTypeId ?? "").toString().trim().toLowerCase();
			if (dynamicType !== normalizedDynamicPageType) return false;
			if (!term) return true;
			const slug = (data?.slug ?? "").toString().toLowerCase();
			const title = (data?.title ?? data?.metadata?.title ?? "").toString().toLowerCase();
			const idValue = (doc.id ?? "").toString().toLowerCase();
			return slug.includes(term) || title.includes(term) || idValue.includes(term);
		});
		return matches.sort((a, b) => {
			const dataA = (a.data ?? {}) as any;
			const dataB = (b.data ?? {}) as any;
			const slugA = (dataA?.slug ?? "").toString().toLowerCase();
			const slugB = (dataB?.slug ?? "").toString().toLowerCase();
			return slugA.localeCompare(slugB);
		});
	}, [dynamicPageSearch, dynamicPages, normalizedDynamicPageType]);

	const formatDynamicPageLabel = (doc: ContentDocument) => {
		const data = (doc.data ?? {}) as any;
		const slug = (data?.slug ?? "").toString().trim();
		const title = (data?.title ?? data?.metadata?.title ?? "").toString().trim();
		const language = (data?.language ?? doc.language ?? "").toString().trim();
		const base = slug || title || doc.id || "Untitled page";
		const titlePart = title && title !== slug ? ` — ${title}` : "";
		const langPart = language ? ` (${language})` : "";
		return `${base}${titlePart}${langPart}`;
	};

	const selectedId = (value ?? "") as string;
	const allowEmpty = field.allowEmpty ?? true;
	const selectDisabled = !editable || dynamicPagesLoading || !normalizedDynamicPageType;

	return (
		<div className="space-y-1">
			<label className="text-xs font-semibold text-slate-600">{field.label}</label>
			{!normalizedDynamicPageType && (
				<p className="text-[11px] text-slate-500">Select a content type first.</p>
			)}
			<input
				type="text"
				value={dynamicPageSearch}
				onChange={(e) => setDynamicPageSearch(e.target.value)}
				placeholder="Filter pages..."
				disabled={selectDisabled}
				className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
			/>
			<select
				value={selectedId}
				onChange={(e) => updateComponentValue(componentIndex, fullPath, e.target.value)}
				disabled={selectDisabled}
				className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
			>
				<option value="">{allowEmpty ? "None" : "Select a page"}</option>
				{filteredDynamicPages.map((doc) => (
					<option key={doc.id ?? formatDynamicPageLabel(doc)} value={doc.id ?? ""}>
						{formatDynamicPageLabel(doc)}
					</option>
				))}
			</select>
			{dynamicPagesLoading && <p className="text-[11px] text-slate-500">Loading dynamic pages…</p>}
			{dynamicPagesError && <p className="text-[11px] text-rose-600">{dynamicPagesError}</p>}
			{field.helper && <p className="text-[11px] text-slate-500">{field.helper}</p>}
		</div>
	);
};

export default DynamicPageSelectControl;
